<template>
  <div class="payment-methods-chart">
    <div v-if="loading" class="text-center py-5">
      <a-spin size="large" />
    </div>
    <div v-else>
      <div class="chart-header">
        <h6 class="chart-title">{{ $t(titleKey) }}</h6>
        <div class="chart-subtitle">{{ $t(subtitleKey) }}</div>
      </div>
      
      <apexchart
        :options="chartOptions"
        :series="chartData.series"
        type="donut"
        height="350"
        class="chart-container"
      />
      
      <!-- Simple Legend -->
      <div class="chart-legend mt-3">
        <div 
          v-for="(item, index) in data" 
          :key="index"
          class="legend-item"
        >
          <div 
            class="legend-color" 
            :style="{ backgroundColor: getColor(index) }"
          ></div>
          <span class="legend-label">{{ getMethodName(item && item.name ? item.name : 'Unknown') }}</span>
          <span class="legend-value">{{ item && item.value ? Number(item.value).toFixed(1) : 0 }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  name: "PaymentMethodsChart",
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    data: {
      type: Array,
      default: () => []
    },
    totalRevenue: {
      type: Number,
      default: 0
    },
    titleKey: {
      type: String,
      default: "financialStats.paymentMethods"
    },
    subtitleKey: {
      type: String,
      default: "financialStats.paymentMethodsBreakdown"
    }
  },
  data() {
    return {
      currency: null,
    };
  },
  created() {
    this.currency = getBuildingCurrency();
  },
  computed: {
    chartData() {
      if (!this.data || this.data.length === 0) {
        return {
          series: []
        };
      }
      
      return {
        series: this.data.map(item => {
          const value = item && item.value !== undefined && item.value !== null ? Number(item.value) : 0;
          return Math.round(value * 100) / 100; // Round to 2 decimal places
        })
      };
    },
    
    chartOptions() {
      return {
        chart: {
          type: 'donut',
          height: 350,
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          }
        },
        colors: ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1'],
        labels: this.data.map(item => this.getMethodName(item && item.name ? item.name : 'Unknown')),
        dataLabels: {
          enabled: true,
          formatter: (val) => {
            return Number(val).toFixed(1) + '%';
          },
          style: {
            fontSize: '12px',
            fontWeight: 'bold',
            colors: ['#fff']
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '60%',
              labels: {
                show: true,
                total: {
                  show: true,
                  showAlways: true,
                  label: this.$t('financialStats.total'),
                  formatter: () => {
                    if (!this.data || this.data.length === 0) return `0 ${this.currency}`;
                    return this.totalRevenue ? this.totalRevenue.toLocaleString() + ` ${this.currency}` : this.$t('financialStats.totalRevenue');
                  }
                }
              }
            }
          }
        },
        legend: {
          show: false
        },
        tooltip: {
          y: {
            formatter: (value, { seriesIndex }) => {
              const item = this.data[seriesIndex];
              if (item && item.value !== undefined && item.value !== null && this.totalRevenue) {
                const amount = (this.totalRevenue * item.value) / 100;
                return Number(amount).toLocaleString() + ` ${this.currency}`;
              }
              return value ? Number(value).toLocaleString() + ` ${this.currency}` : `0 ${this.currency}`;
            }
          }
        }
      };
    }
  },
  methods: {
    getColor(index) {
      const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1'];
      return colors[index % colors.length];
    },
    
    getMethodName(method) {
      return this.$t('recette.paymentMethod.' + method);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/shared.scss';
// All styles are in shared.scss
</style>